import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { newTheme, originalTheme } from '../theme';

export const ACCENT_PALETTE = [
  { color: '#059669', label: 'Sage' },
  { color: '#0EA5E9', label: 'Sky' },
  { color: '#8B5CF6', label: 'Violet' },
  { color: '#EF4444', label: 'Red' },
  { color: '#F97316', label: 'Orange' },
  { color: '#EAB308', label: 'Amber' },
  { color: '#EC4899', label: 'Pink' },
  { color: '#14B8A6', label: 'Teal' },
];

const DEFAULT_ACCENT = '#059669';

interface ThemeContextValue {
  isNewTheme: boolean;
  toggleTheme: () => void;
  accentColor: string;
  setAccentColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  isNewTheme: true,
  toggleTheme: () => {},
  accentColor: DEFAULT_ACCENT,
  setAccentColor: () => {},
});

export const useAppTheme = () => useContext(ThemeContext);

export function AppThemeProvider({ children }: { children: React.ReactNode }) {
  const [isNewTheme, setIsNewTheme] = useState<boolean>(() => {
    return localStorage.getItem('app-theme') !== 'original';
  });

  const [accentColor, setAccentColorState] = useState<string>(() => {
    return localStorage.getItem('accent-color') || DEFAULT_ACCENT;
  });

  const toggleTheme = useCallback(() => {
    setIsNewTheme((prev) => {
      const next = !prev;
      localStorage.setItem('app-theme', next ? 'new' : 'original');
      return next;
    });
  }, []);

  const setAccentColor = useCallback((color: string) => {
    setAccentColorState(color);
    localStorage.setItem('accent-color', color);
  }, []);

  const activeTheme = useMemo(() => {
    if (!isNewTheme) return originalTheme;
    return createTheme(newTheme, {
      palette: {
        info: {
          main: accentColor,
          light: accentColor,
          dark: accentColor,
          contrastText: '#ffffff',
        },
      },
    });
  }, [isNewTheme, accentColor]);

  return (
    <ThemeContext.Provider value={{ isNewTheme, toggleTheme, accentColor, setAccentColor }}>
      <ThemeProvider theme={activeTheme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}
